"use client";
import get_videos_helper from "@/app/api/data/videos/get_videos_helper";
import ButtonNF from "@/app/ui/forms/ButtonNF";
import { Skeleton } from "@/app/ui/imported/skeleton";
import BlurFade from "@/app/ui/magicui/blur-fade";
import { ActionResponseType, ResponseError } from "@/lib/definitions";
import { cn } from "@/lib/utils";
import { Season } from "@prisma/client";
import { ReactNode, useEffect, useState } from "react";
import { toast } from "sonner";
import useSWR, { SWRConfiguration_Custom } from "swr";
import VideoItem from "../VideoItem/VideoItem";

const videosPerPage = 6;

export interface VideoSectionProps {
    initialVideos: VideoArr
    ytid?: string,
    season?: Season,
    sortBy: "recent" | "popular"
}

type VideoArr = ActionResponseType<typeof get_videos_helper>;

/** client component for rendering video list - should get initial data from server */
export default function VideoSection_client({ ytid, season, sortBy, initialVideos }: VideoSectionProps) {
    const [hasMorePages, setHasMorePages] = useState(initialVideos.length >= videosPerPage);
    const [page, setPage] = useState(0);
    const [videos, setVideos] = useState<Set<VideoArr[0]>>(new Set(initialVideos || []));
    const { data, isLoading } = useSWR<VideoArr | ResponseError>(`/api/data/videos?ytid=${ytid || ""}&se=${season?.shortName || ""}&sortBy=${sortBy}&page=${page}`,
        null,
        {
            "dedupingInterval": 1000 * 60 * 60, //keep in client cache for 1h (resets on client refresh)
            "revalidateOnMount": false,
            "revalidateIfStale": false,
            "revalidateOnReconnect": false,
        } as SWRConfiguration_Custom)

    //handle response from server / swr cache
    useEffect(() => {
        //if no data
        if (!data) return;

        //check if error
        if ("error" in data) {
            toast.error(data.error)
            return;
        }

        //no more videos
        if (data.length < videosPerPage) {
            setHasMorePages(false)
        }

        if (data.length == 0) {
            toast.info("אין עוד סרטונים להציג..")
            return;
        }

        setVideos((prev) => new Set([...prev, ...data]));
    }, [data])

    if (!videos || videos.size == 0)
        return <p>לא נמצאו סרטונים. אולי עוד מעט יהיה :)</p>
    return <>
        <VideoSectionGrid initialVideos_count={ initialVideos.length }>
            { Array.from(videos).map((v, i) => {
                const isInitialVideo = i < videosPerPage;
                //if initial video render as is
                if (isInitialVideo)
                    return <VideoItem key={ v.id } video={ v } textBelow />
                //otherwise render with blurFade
                return <BlurFade key={ v.id } delay={ i % 6 * 0.1 } inView>
                    <VideoItem video={ v } textBelow />
                </BlurFade>

            }) }
            { isLoading && <VideoSectionSkeleton /> }
        </VideoSectionGrid>
        { hasMorePages &&
            <ButtonNF isLoading={ isLoading }
                className="m-auto block w-11/12 max-w-screen-sm max-sm:text-4xl"
                color="nfBlue"
                onClick={ () => setPage(pre => pre + 1) }>טען עוד</ButtonNF>
        }
    </>
}

/** grid layout for videoSection - used for skeleton loader &  VideoSection_client*/
export function VideoSectionGrid({ children, initialVideos_count = 6 }: { children: ReactNode, initialVideos_count?: number }) {
    return <div className={ cn('grid gap-4 gap-y-6 sm:gap-6 p-2 sm:p-4 pt-4',
        "grid-cols-2 md:grid-cols-3 xl:grid-cols-6",
        initialVideos_count < 6 && `flex [&>span]:flex-1 [&>span]:max-w-[50%] md:[&>span]:max-w-[25%] justify-center`
    ) }>
        { children }
    </div>

}
export function VideoSectionSkeleton() {
    return Array(videosPerPage).fill(0).map((v, index) =>
        <div key={ index }>
            <Skeleton className="aspect-video rounded" />
            <Skeleton><h3 role="heading" className="mt-2 px-1 text-sm sm:text-md rounded-md leading-[1.2em_!important]">..</h3></Skeleton>
        </div>)

}